import {
  Alert,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
} from '@mui/material';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from '@efexteam/components';
import DataLoadingWrapper from '../../../../components/common/DataLoadingWrapper';
import { StepsControl } from '../../constants';
import { useFormData } from '../context';

interface ITermsAndConditions {
  formStep: number;
  error?: string;
  errorCode?: string;
  loading: boolean;
  nextFormStep: () => void;
  prevStep: () => void;
}

interface FormData {
  acceptedTerms: boolean;
}

const TermsAndConditions = ({
  formStep,
  error,
  errorCode,
  loading,
  nextFormStep,
  prevStep,
}: ITermsAndConditions) => {
  const { setFormValues, data } = useFormData();
  const { t } = useTranslation();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormData>({ mode: 'all' });

  const onSubmit = handleSubmit((values) => {
    setFormValues(values as unknown as Record<string, unknown>);
  });

  const handleBack = () => {
    prevStep();
  };

  useEffect(() => {
    if (data.acceptedTerms) {
      nextFormStep();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  if (formStep !== StepsControl.TermsInformation) return null;

  return (
    <>
      <Typography variant="h4" className="!mt-4 !mb-5 text-center">
        {t('regTermsInformation')}
      </Typography>

      <form onSubmit={onSubmit} autoComplete="off">
        <Grid
          container
          rowSpacing={2}
          columnSpacing={{ xs: 2, sm: 2, md: 3 }}
          style={{
            minHeight: '50vh',
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <Grid
            item
            xs={12}
            style={{
              height: '100%',
            }}
          >
            <FormControl fullWidth>
              <Controller
                name="acceptedTerms"
                control={control}
                rules={{ required: true }}
                render={({ field }) => (
                  <>
                    <Typography variant="subtitle1">
                      {t('regTermsConfirmation1')} {t('regTermsConfirmation2')}
                      {t('regTermsConfirmation3')}
                      <a
                        href="https://legal.efexpay.com/"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {' '}
                        <Typography
                          component="span"
                          variant="subtitle1"
                          className="text-blue-500 hover:font-semibold hover:text-blue-700 inline cursor-pointer underline"
                        >
                          {t('regTermsConfirmation4')}
                        </Typography>
                      </a>
                      {t('regTermsConfirmation5')}
                    </Typography>

                    <FormControlLabel
                      control={<Checkbox {...field} />}
                      label={t('userAcceptTerms')}
                    />
                    {errors.acceptedTerms && (
                      <Typography variant="body2" className="text-orange-600">
                        {t('pleaseAcceptTerms')}
                      </Typography>
                    )}

                    <Alert severity="success" className="mt-4">
                      {t('regTermsConfirmationDisclaimer')}
                    </Alert>
                  </>
                )}
              />
              {error && (
                <Typography variant="body2" className="text-orange-600">
                  {t(error)}
                </Typography>
              )}
              {errorCode && (
                <Typography variant="body2" className="text-orange-600">
                  Code: {errorCode}
                </Typography>
              )}
            </FormControl>
          </Grid>
        </Grid>
        <Grid item xs={12} className="flex justify-around">
          {formStep > 0 && (
            <Button
              appearance="secondary"
              className="mt-4"
              size="large"
              onClick={handleBack}
              type="button"
            >
              {t('backBtn')}
            </Button>
          )}
          <DataLoadingWrapper loadingData={loading} variant="spinner">
            <Button
              appearance="primary"
              size="large"
              className="mt-4"
              type="submit"
            >
              {t('register')}
            </Button>
          </DataLoadingWrapper>
        </Grid>
      </form>
    </>
  );
};

export default TermsAndConditions;
